import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { DateTime } from 'luxon';
import { strings } from '/imports/strings';
import { formatDateTime } from '/imports/utils';
import { useMethod } from '/imports/hooks';
import { Loading } from '/imports/ui/Loading';
import { Icon, IconKey } from '/imports/ui/Icon';
import { css } from '/imports/ui/css';
import { Project, projectStatus } from '/imports/api/projects';
import { Dict } from '/imports/types';
import {
  modelClass as activityModelClass,
  ProjectActivity,
  ProjectActivityCategory,
  ProjectActivityCategoryByType,
  ProjectActivityType,
} from '/imports/api/projectActivities';

const TimelineStyled = styled.div`
  margin-top: 1rem;
  .filters {
    display: flex;
    gap: 0.5rem;
    margin-bottom: 1rem;
    flex-wrap: wrap;
    .chip {
      padding: 0.25rem 0.75rem;
      border-radius: 1rem;
      border: 1px solid ${css.grey7};
      cursor: pointer;
      font-size: 0.875rem;
      &.active {
        background-color: ${css.primaryColor};
        color: ${css.white};
        border-color: ${css.primaryColor};
      }
    }
  }
  .loading-row {
    display: flex;
    justify-content: center;
    padding: 2rem 0;
  }
  .empty {
    color: ${css.grey5};
    padding: 1.5rem 0;
    text-align: center;
  }
  ul.timeline {
    list-style: none;
    padding-left: 0;
    margin: 0;
    border-left: 2px solid ${css.grey8};
    li {
      position: relative;
      padding: 0.5rem 0 1rem 1.5rem;
      .marker {
        position: absolute;
        left: -1.0625rem;
        top: 0.625rem;
        width: 2rem;
        height: 2rem;
        border-radius: 50%;
        background-color: ${css.white};
        border: 2px solid ${css.grey7};
        display: flex;
        align-items: center;
        justify-content: center;
        .icon {
          width: 1rem;
          height: 1rem;
          svg {
            width: 1rem;
            height: 1rem;
          }
        }
        &.lifecycle { border-color: ${css.primaryColor}; }
        &.edits { border-color: ${css.orange}; }
        &.enrichment { border-color: ${css.brightBlue}; }
        &.documents { border-color: ${css.green}; }
        &.integrations { border-color: ${css.grey5}; }
      }
      .row {
        display: flex;
        gap: 0.75rem;
        align-items: baseline;
        flex-wrap: wrap;
        .label {
          font-weight: 600;
        }
        .summary {
          color: ${css.grey3};
        }
        .navigate {
          background: none;
          border: none;
          padding: 0;
          font-size: 0.8125rem;
          color: ${css.primaryColor};
          cursor: pointer;
        }
        .meta {
          color: ${css.grey5};
          font-size: 0.875rem;
          margin-left: auto;
        }
      }
      .details-toggle {
        margin-top: 0.25rem;
        background: none;
        border: none;
        padding: 0;
        font-size: 0.8125rem;
        color: ${css.primaryColor};
        cursor: pointer;
      }
      pre.details {
        margin-top: 0.5rem;
        padding: 0.75rem;
        background-color: ${css.grey9};
        border-radius: 0.25rem;
        font-size: 0.8125rem;
        white-space: pre-wrap;
        word-break: break-word;
      }
      ul.step-list {
        margin: 0.5rem 0 0;
        padding: 0.5rem 0.75rem 0.5rem 1.25rem;
        background-color: ${css.grey9};
        border-radius: 0.25rem;
        list-style: disc;
        font-size: 0.8125rem;
        li {
          padding: 0.15rem 0;
          .step-name { font-weight: 600; margin-right: 0.5rem; }
          .step-data { color: ${css.grey3}; }
        }
      }
    }
  }
`;

const ActivityTypeIcon: { [k in ProjectActivityType]: IconKey } = {
  'created': 'check',
  'deleted': 'cancel',
  'assigneeChanged': 'person',
  'statusChanged': 'tune',
  'fieldsEdited': 'tune',
  'enrichmentStarted': 'settings',
  'enrichmentCompleted': 'circleCheck',
  'enrichmentFailed': 'warning',
  'enrichmentStep': 'settings',
  'documentAdded': 'documentSearch',
  'documentApproved': 'circleCheck',
  'documentDeleted': 'cancel',
  'quoteCreated': 'documentSearch',
  'quoteUpdated': 'tune',
  'quoteDeleted': 'cancel',
  'consumptionMetersApplied': 'electricMeter',
  'hubspotPortalRecordCreated': 'send',
  'epcPriceModelRun': 'percent',
};

/** Localized label for an activity type. */
const ActivityTypeLabel = (type: ProjectActivityType): string => {
  switch (type) {
    case 'created': return strings.ProjectCreated();
    case 'deleted': return strings.ProjectDeleted();
    case 'assigneeChanged': return strings.AssigneeChanged();
    case 'statusChanged': return strings.StatusChanged();
    case 'fieldsEdited': return strings.FieldsEdited();
    case 'enrichmentStarted': return strings.EnrichmentStarted();
    case 'enrichmentCompleted': return strings.EnrichmentCompleted();
    case 'enrichmentFailed': return strings.EnrichmentFailed();
    case 'enrichmentStep': return strings.EnrichmentStep();
    case 'documentAdded': return strings.DocumentAdded();
    case 'documentApproved': return strings.DocumentApproved();
    case 'documentDeleted': return strings.DocumentDeleted();
    case 'quoteCreated': return strings.QuoteCreated();
    case 'quoteUpdated': return strings.QuoteUpdated();
    case 'quoteDeleted': return strings.QuoteDeleted();
    case 'consumptionMetersApplied': return strings.ConsumptionMetersApplied();
    case 'hubspotPortalRecordCreated': return strings.HubspotPortalRecordCreated();
    case 'epcPriceModelRun': return strings.EpcPriceModelRun();
    default: return type;
  }
};

/**
 * Short human summary describing an activity's payload.
 * @param a - the activity
 * @returns summary text, or empty string when none applies
 */
const activitySummary = (a: ProjectActivity): string => {
  const data = a.data || {};
  switch (a.type) {
    case 'statusChanged':
      return `${projectStatus({ status: data.from }) || ''} → ${projectStatus({ status: data.to }) || ''}`;
    case 'assigneeChanged':
      return `${data.from || '—'} → ${data.to || '—'}`;
    case 'fieldsEdited':
      return Object.keys(data.changes || {}).map(humanizeKey).join(', ');
    case 'documentAdded':
    case 'documentApproved':
    case 'documentDeleted':
      return data.name || data.documentId || '';
    case 'enrichmentStep':
      return humanizeKey(data.step || '');
    case 'enrichmentCompleted': {
      const stepCount = (data.steps || []).length;
      const seconds = data.ms ? Math.round(data.ms / 1000) : undefined;
      if (data.cascaded) return humanizeKey(data.steps?.[0]?.step || '');
      return `${stepCount} step${stepCount === 1 ? '' : 's'}${seconds !== undefined ? ` · ${seconds}s` : ''}`;
    }
    case 'enrichmentFailed':
      return data.error || '';
    case 'consumptionMetersApplied':
      return `${(data.meterIds || []).length} meter(s)`;
    default:
      return '';
  }
};

/** Display name for whoever (user/job/system) produced the activity. */
const actorLabel = (a: ProjectActivity): string => {
  if (a.actor === 'user') return a.actorName || a.createdByName || '';
  if (a.actor === 'job') return strings.JobActor();
  return strings.SystemActor();
};

interface FilterChip { id: 'all' | ProjectActivityCategory; label: string }

const filterChips: FilterChip[] = [
  { id: 'all', label: strings.ActivityFilterAll() },
  { id: 'lifecycle', label: strings.ActivityFilterLifecycle() },
  { id: 'enrichment', label: strings.ActivityFilterEnrichment() },
  { id: 'documents', label: strings.ActivityFilterDocuments() },
  { id: 'edits', label: strings.ActivityFilterEdits() },
];

/**
 * Humanizes a camelCase key, preferring a matching strings.ts getter over a Title-Case split.
 * @param key - camelCase field/step key
 * @returns readable label
 */
const humanizeKey = (key: string): string => {
  if (!key) return '';
  const pascalKey = key.charAt(0).toUpperCase() + key.slice(1);
  const fn = (strings as Dict<any>)[pascalKey];
  if (typeof fn === 'function') return fn();
  return pascalKey.replace(/([A-Z])/g, ' $1').replace(/^ /, '');
};

/**
 * Flattens an enrichment step's fields into a comma-separated label=value string.
 * @param step - step data object
 * @returns formatted field list
 */
const renderStepData = (step: Dict<any>): string => {
  const entries: string[] = [];
  for (const key of Object.keys(step)) {
    if (key === 'step') continue;
    const value = step[key];
    if (value === undefined || value === null) continue;
    const label = humanizeKey(key);
    if (typeof value === 'object') entries.push(`${label}=${JSON.stringify(value)}`);
    else entries.push(`${label}=${value}`);
  }
  return entries.join(', ');
};

/** Project-view tab to deep-link to for a given activity type, if any. */
const navigateTabForType = (type: ProjectActivityType): string | undefined => {
  switch (type) {
    case 'documentAdded':
    case 'documentApproved':
    case 'documentDeleted':
    case 'consumptionMetersApplied':
      return 'dataroom';
    case 'quoteCreated':
    case 'quoteUpdated':
    case 'quoteDeleted':
      return 'quotes';
    default:
      return undefined;
  }
};

/** Filterable, expandable activity timeline for a project. */
export const ProjectViewTimeline = (props: { project: Project, onNavigate?: (tab: string) => void }) => {
  const { project, onNavigate } = props;
  const [filter, setFilter] = useState<FilterChip['id']>('all');
  const [expanded, setExpanded] = useState<{ [id: string]: boolean }>({});
  const query = useMemo(() => ({ projectId: project._id }), [project._id]);
  const [response, loading] = useMethod(
    activityModelClass.listMethod,
    { query, limit: 200, sort: { createdAt: -1 } },
    [project._id],
  );
  const activities: ProjectActivity[] = response?.documents || [];
  const visibleActivities = activities.filter((a) => {
    if (filter === 'all') return true;
    return ProjectActivityCategoryByType[a.type] === filter;
  });
  return (
    <TimelineStyled className="project-timeline">
      <div className="filters">
        {
          filterChips.map((chip) => (
            <a
              key={chip.id}
              href="#"
              className={`chip ${filter === chip.id ? 'active' : ''}`}
              onClick={(e) => { e.preventDefault(); setFilter(chip.id); }}
            >
              {chip.label}
            </a>
          ))
        }
      </div>
      {
        loading ?
          <div className="loading-row"><Loading /></div>
          :
          !visibleActivities.length ?
            <div className="empty">{strings.NoActivityYet()}</div>
            :
            <ul className="timeline">
              {
                visibleActivities.map((a) => {
                  const summary = activitySummary(a);
                  const data = a.data || {};
                  const steps: Dict<any>[] = data.steps || [];
                  const hasDetails = !!a.data && Object.keys(a.data).length > 0;
                  const category = ProjectActivityCategoryByType[a.type] || 'lifecycle';
                  const navigateTab = navigateTabForType(a.type);
                  return (
                    <li key={a._id}>
                      <span className={`marker ${category}`}><Icon icon={ActivityTypeIcon[a.type] || 'check'} /></span>
                      <div className="row">
                        <span className="label">{ActivityTypeLabel(a.type)}</span>
                        {summary ? <span className="summary">{summary}</span> : null}
                        {
                          navigateTab && onNavigate ?
                            <button className="navigate" onClick={() => onNavigate(navigateTab)}>{strings.View()}</button>
                            : null
                        }
                        <span className="meta">
                          {actorLabel(a)} · {a.createdAt ? formatDateTime(a.createdAt) : ''}
                          {a.createdAt ? <> ({DateTime.fromJSDate(a.createdAt).toRelative()})</> : null}
                        </span>
                      </div>
                      {
                        hasDetails ?
                          <button
                            className="details-toggle"
                            onClick={() => setExpanded({ ...expanded, [a._id]: !expanded[a._id] })}
                          >
                            {expanded[a._id] ? strings.HideDetails() : strings.ShowDetails()}
                          </button>
                          : null
                      }
                      {
                        hasDetails && expanded[a._id] ?
                          steps.length ?
                            <ul className="step-list">
                              {steps.map((s, i) => (
                                <li key={i}>
                                  <span className="step-name">{humanizeKey(s.step)}</span>
                                  <span className="step-data">{renderStepData(s)}</span>
                                </li>
                              ))}
                            </ul>
                            :
                            <pre className="details">{JSON.stringify(a.data, null, 2)}</pre>
                          : null
                      }
                    </li>
                  );
                })
              }
            </ul>
      }
    </TimelineStyled>
  );
};
